/* eslint-disable max-lines-per-function */
import React from "react";
import * as yup from "yup";
import {
  Menu, MenuItem, Checkbox, DialogContent, Typography, Tooltip, Grid,
} from "@mui/material";
import { bindMenu, bindTrigger } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Form, Formik } from "formik";
import { Button, Modal } from "components/haven-design";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { DialogActions } from "components/haven-design/modal/dialog";
import { Viewer } from "components/auth/types";
import { useInterestsTable } from "../interests-table/table-context";
import { useSidebarFilters } from "../sidebar-filters";
import styles from "./button-select-interests.module.css";

const SelectAllButton: React.FC<{isChecked: boolean; viewer: Pick<Viewer, "id">; currentListId: string}> = () => {
  const {
    replaceAllSelection,
    fullPageOrAllPagesSelected,
    selectedSomeInterests,
    selectedPageRange,
    selectPageRange,
    selectAllInterests,
    tableResults,
    totalTableResults,
  } = useInterestsTable();
  const { pageState: { page: currentPage, nbPages } } = useSidebarFilters();

  const { from: rangeFrom, to: rangeTo } = selectedPageRange;
  const [rangeModalOpen, setRangeModalOpen] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const popupState = usePopupState({ variant: "popover", popupId: "select-interests-popup-menu" });
  const visibleIds = tableResults.map((result) => result.id);

  // Prefer set(true/false) over toggle — a double-submit or stale toggle can reopen the modal.
  const openRangeModal = () => setRangeModalOpen(true);
  const closeRangeModal = () => setRangeModalOpen(false);
  const handleSelectCurrentPage = () => {
    replaceAllSelection(visibleIds);
  };
  const handleSelectAll = () => selectAllInterests(visibleIds);

  const handleDeselectAll = () => {
    replaceAllSelection([]);
  };
  // checkbox next to dropdown
  const handleTogglePage = () => {
    if (fullPageOrAllPagesSelected) {
      handleDeselectAll();
    } else {
      handleSelectCurrentPage();
    }
  };
  const doSelectPageRange = ({ from, to }: {from: string, to: string}) => {
    if (from === "" && to === "") return;
    const fromNumber = from === "" ? 0 : parseInt(from, 10) - 1;
    const toNumber = to === "" ? nbPages - 1 : parseInt(to, 10) - 1;

    const outOfRange = currentPage < fromNumber || currentPage > toNumber;
    selectPageRange({
      visibleIds: outOfRange ? [] : visibleIds, from: fromNumber, to: toNumber, totalPages: nbPages,
    });
    closeRangeModal();
  };

  const { onClick, ...rest } = bindTrigger(popupState);
  const updatedOnClick = (event: React.MouseEvent<Element>) => {
    setTooltipOpen(false);
    onClick(event);
  };
  const updatedTrigger = { ...rest, onClick: updatedOnClick };

  // Keep Menu outside Tooltip — tooltip z-index sits above menus and can block clicks.
  // Selection after exit — updating mid-transition strands an invisible MUI backdrop.
  const pendingMenuAction = React.useRef<(() => void) | null>(null);
  const closeMenuThen = (action: () => void) => {
    pendingMenuAction.current = action;
    popupState.close();
  };
  const menuItems = [
    {
      id: "select-interests-current-page",
      label: `Select Current Page (${tableResults.length})`,
      action: handleSelectCurrentPage,
    },
    {
      id: "select-interests-all",
      label: `Select All (${totalTableResults})`,
      action: handleSelectAll,
    },
    { id: "select-interests-page-range", label: "Select Page Range", action: openRangeModal },
    { id: "select-interests-none", label: "Select None", action: handleDeselectAll },
  ];

  return (
    <>
      <Tooltip
        title="Select Applications"
        open={tooltipOpen && !popupState.isOpen}
        disableHoverListener={popupState.isOpen}
        slotProps={{ transition: { timeout: 0 } }}
        onOpen={() => {
          if (!popupState.isOpen) setTooltipOpen(true);
        }}
        onClose={() => setTooltipOpen(false)}>
        <div className={styles.container}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
              justifyContent: "space-between",
              padding: "0 10px",
            }}
            {...updatedTrigger}
            className={styles.icon}>
            <Checkbox
              size="small"
              checked={fullPageOrAllPagesSelected}
              indeterminate={selectedSomeInterests && !fullPageOrAllPagesSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePage();
              }}
              style={{ padding: 0 }}
            />
            <span style={{
              fontWeight: 800,
              color: "#B8B8B8",
              textAlign: "center",
              flex: 1,
              paddingLeft: 3,
            }}>
              Select
            </span>
            <ExpandMoreIcon fontSize="small" id="select-open-arrow" />
          </div>
        </div>
      </Tooltip>
      <Menu
        {...bindMenu(popupState)}
        // TransitionProps (not slotProps.transition) so Menu can still chain its own onExited.
        TransitionProps={{
          onExited: () => {
            const action = pendingMenuAction.current;
            pendingMenuAction.current = null;
            action?.();
          },
        }}
        slotProps={{ list: { id: "select-interests-menu" } }}>
        {menuItems.map(({ id, label, action }) => (
          <MenuItem key={id} id={id} onClick={() => closeMenuThen(action)}>{label}</MenuItem>
        ))}
      </Menu>
      <Modal
        isOpen={rangeModalOpen}
        onRequestClose={closeRangeModal}
        fullWidth
        maxWidth="sm"
        title="Select Page Range">
        <Formik
          initialValues={{
            from: (rangeFrom ? `${rangeFrom + 1}` : (currentPage + 1).toString()),
            to: (rangeTo === null ? `${nbPages}` : `${rangeTo + 1}`),
          }}
          validationSchema={yup.object().shape({
            from: yup.number().min(1, "Must be at least 1")
              .max(nbPages, "Must be less or equal than the number of pages"),
            to: yup.number().min(1, "Must be at least 1")
              .max(nbPages, "Must be less or equal than the number of pages")
              .when("from", ([from], schema) =>
                (from ? schema.min(from, "Must be greater or equal to from") : schema)),
          })}

          onSubmit={doSelectPageRange}
        >
          {({ values, errors }) => (<>
            <DialogContent>
              <Form id="select-page-range">
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <InputWithErrorLabel
                      fullWidth
                      label="From"
                      type="number"
                      name="from"
                      value={values.from}
                      slotProps={{ htmlInput: { min: 1, max: nbPages } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <InputWithErrorLabel
                      fullWidth
                      label="To"
                      type="number"
                      name="to"
                      value={values.to}
                      slotProps={{ htmlInput: { min: values.from ? (parseInt(values.from, 10)) : "1", max: nbPages } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">
                      Current page: {currentPage + 1}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total pages: {nbPages}
                    </Typography>
                  </Grid>
                </Grid>
              </Form>
            </DialogContent>
            <DialogActions>
              <Button type="submit"
                form="select-page-range"
                color="primary"
                size="L"
                disabled={!!errors.from || !!errors.to}
              >Select</Button>
            </DialogActions>
          </>
          )}
        </Formik>
      </Modal>
    </>
  );
};


export default SelectAllButton;
